import React, {useCallback, useRef, useState} from 'react';
import Dialog from './Dialog';
import {CameraIcon, CheckCheckIcon} from './icons';

interface PurchaseConfirmDialogProps {
    /** Заголовок пункта списка — показывается в шапке модалки. */
    itemTitle: string;
    /** Плановая цена — префилл поля цены (может отсутствовать). */
    plannedPriceRub: number | null;
    /** Подтверждение: фактическая цена (>0) и опциональное фото (base64 без data-URI). */
    onConfirm: (priceRub: number, photoBase64: string | null) => Promise<void>;
    onClose: () => void;
}

/**
 * Модалка подтверждения покупки (MR-3 purchase-loop-closure, FR-9): фактическая цена
 * (prefilled из плановой) + опциональное фото drag&drop (как в PurchaseAnalysisPage).
 * Одна primary-кнопка «Подтвердить покупку» (styleguide: одна primary на decision region).
 * Успех создаёт вещь в гардеробе — родитель показывает ссылку на карточку.
 */
const PurchaseConfirmDialog: React.FC<PurchaseConfirmDialogProps> = (
    {itemTitle, plannedPriceRub, onConfirm, onClose}
) => {
    const fileRef = useRef<HTMLInputElement>(null);
    const [price, setPrice] = useState(plannedPriceRub != null ? String(plannedPriceRub) : '');
    const [preview, setPreview] = useState<string | null>(null);
    const [base64, setBase64] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(false);

    const handleFile = useCallback((file: File) => {
        const reader = new FileReader();
        reader.onload = e => {
            const dataUrl = e.target?.result as string;
            setPreview(dataUrl);
            setBase64(dataUrl.split(',')[1]);
        };
        reader.readAsDataURL(file);
    }, []);

    const onDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file?.type.startsWith('image/')) handleFile(file);
    }, [handleFile]);

    const priceValue = Number(price);
    const priceValid = price.trim() !== '' && Number.isFinite(priceValue) && priceValue > 0;

    const submit = async () => {
        if (!priceValid || saving) return;
        setSaving(true);
        setError(false);
        try {
            await onConfirm(priceValue, base64);
        } catch {
            setError(true);
            setSaving(false);
        }
    };

    return (
        <Dialog
            open
            onClose={onClose}
            title="Подтвердить покупку"
            busy={saving}
            data-testid="purchase-confirm-dialog"
            panelClassName="bg-white dark:bg-ink-800 rounded-2xl shadow-xl max-w-md w-full flex flex-col outline-none"
            headerClassName="flex items-center justify-between gap-3 p-4 border-b border-gray-200 dark:border-ink-700"
            bodyClassName="p-4 space-y-4"
            footerClassName="flex gap-2 p-4 border-t border-gray-200 dark:border-ink-700"
            footer={
                <>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!priceValid || saving}
                        className="flex-1 inline-flex min-h-[44px] items-center justify-center gap-2 px-4 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                        data-testid="purchase-confirm-submit"
                    >
                        <CheckCheckIcon size={18}/> {saving ? 'Подтверждаю…' : 'Подтвердить покупку'}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="min-h-[44px] px-4 rounded-xl border border-gray-200 dark:border-ink-700 text-gray-600 dark:text-gray-300 hover:border-brand-500"
                    >
                        Отмена
                    </button>
                </>
            }
        >
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        «{itemTitle}» появится в гардеробе с атрибутами подбора; цена уменьшит бюджет месяца.
                    </p>

                    <label className="block">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Фактическая цена, ₽
                        </span>
                        <input
                            type="number"
                            min="1"
                            value={price}
                            onChange={e => setPrice(e.target.value)}
                            aria-label="Фактическая цена"
                            className="mt-1 w-full min-h-[44px] px-3 rounded-xl border border-gray-200 dark:border-ink-700 bg-white dark:bg-ink-900 text-gray-900 dark:text-gray-100"
                        />
                    </label>

                    <div
                        onDrop={onDrop}
                        onDragOver={e => e.preventDefault()}
                        onClick={() => fileRef.current?.click()}
                        data-testid="purchase-photo-dropzone"
                        className="border-2 border-dashed border-gray-300 dark:border-ink-600 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-brand-500 transition min-h-[120px]"
                    >
                        {preview ? (
                            <img src={preview} alt="Фото покупки" className="max-h-40 rounded-xl object-contain"/>
                        ) : (
                            <div className="text-center p-4">
                                <CameraIcon className="w-8 h-8 text-gray-300 dark:text-ink-600 mx-auto mb-1"/>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Фото вещи (необязательно) — нажмите или перетащите
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                    AI дозаполнит атрибуты по фото
                                </p>
                            </div>
                        )}
                    </div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        aria-label="Фото покупки"
                        onChange={e => {
                            const f = e.target.files?.[0];
                            if (f) handleFile(f);
                        }}
                    />

                    {error && (
                        <p className="text-sm text-rose-600 dark:text-rose-400" data-testid="purchase-confirm-error">
                            Не удалось подтвердить покупку. Попробуйте ещё раз.
                        </p>
                    )}
        </Dialog>
    );
};

export default PurchaseConfirmDialog;
